import { yupResolver } from '@hookform/resolvers/yup';
import { SIGNUP } from 'constants/routes';
import { VerifyUserDomainResponse, useAuth } from 'hooks/useAuth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import * as Yup from 'yup';

const LoginFormSchema = Yup.object().shape({
  username: Yup.string().required('Please enter your email address').email('Enter a valid email'),
  password: Yup.string().required('Please enter password'),
  rememberMe: Yup.boolean(),
});

export type LoginFormData = {
  username: string;
  password: string;
  rememberMe?: boolean;
};

export function useLogin() {
  const navigate = useNavigate();
  const { verifyDomain, login } = useAuth();
  const [verifyDomainResponse, setVerifyDomainResponse] = useState<
    VerifyUserDomainResponse | undefined
  >(undefined);
  const loginFormData = useForm<LoginFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(
      verifyDomainResponse
        ? LoginFormSchema
        : Yup.object().shape({
            username: Yup.string()
              .required('Please enter your email address')
              .email('Enter a valid email'),
          }),
    ),
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false,
    },
  });
  const { getValues } = loginFormData;
  const [showUnknownDomainModal, setShowUnknownDomainModal] = useState(false);

  const onSubmit = async (values: LoginFormData) => {
    /*
      DevNote: 
        This explicit promise is to help react-hook-form to set it's form submission state. Currently, react-hook-form isn't supporting manual mutation of form submission state.
    */
    const authPromise = new Promise((resolve) => {
      if (verifyDomainResponse) {
        login(values, {
          onSuccess: () => {
            resolve(true);
          },
          onError: (error) => {
            resolve(true);
            handleError(error);
          },
        });
      } else {
        verifyDomain(values.username, {
          onError: (error) => {
            resolve(true);
            handleError(error);
          },
          onSuccess: ({ data }) => {
            if (typeof data === 'object') {
              if (data.loginType?.includes('SSO')) {
                window.location.href = data?.redirectUrl || '';
              } else if (data?.messageCode === 'NEW_USER') {
                navigate(SIGNUP, {
                  state: { verifyDomainResponse: data, email: getValues().username },
                });
              } else {
                setVerifyDomainResponse(data);
              }
            }
            resolve(true);
          },
        });
      }
    });
    await authPromise;
  };

  const handleError = (error: any) => {
    if (error.response.data?.errorMessages?.[0]?.errorCode === "Company Domain doesn't exist") {
      setShowUnknownDomainModal(true);
    } else {
      toast.error(catchErrorMessage(error));
    }
  };

  return {
    loginFormData,
    showUnknownDomainModal,
    verifyDomainResponse,
    onSubmit,
    setShowUnknownDomainModal,
    setVerifyDomainResponse,
  };
}
